import type { Link, Root, Text } from 'mdast';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

import {
  DOCUMENT_LINK_PATTERN,
  buildDocumentLinkAttributes,
  buildDocumentLinkLabel,
  buildDocumentLinkUrl,
} from '@/utils/documentLinks';

type Options = {
  organizationId?: string;
};

type ReplacementNode = Text | Link;

const buildLinkNode = (documentId: string, page: string | undefined, organizationId: string): Link => {
  const url = buildDocumentLinkUrl(organizationId, documentId, page);
  const label = buildDocumentLinkLabel(page);
  const attributes = buildDocumentLinkAttributes(documentId, page);

  return {
    type: 'link',
    url,
    children: [
      {
        type: 'text',
        value: label,
      },
    ],
    data: {
      hProperties: {
        target: '_blank',
        rel: 'noopener noreferrer',
        ...attributes,
      },
    },
  };
};

const buildReplacementNodes = (value: string, organizationId: string): ReplacementNode[] | null => {
  const matches = [...value.matchAll(DOCUMENT_LINK_PATTERN)];

  if (matches.length === 0) {
    return null;
  }

  const nodes: ReplacementNode[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    const start = match.index ?? 0;

    if (start > lastIndex) {
      nodes.push({
        type: 'text',
        value: value.slice(lastIndex, start),
      });
    } else if (nodes.length > 0 && nodes[nodes.length - 1].type === 'link') {
      // Add spacing between consecutive links when no text separates placeholders.
      nodes.push({
        type: 'text',
        value: ' ',
      });
    }

    const documentId = match[1];
    const page = match[2];

    nodes.push(buildLinkNode(documentId, page, organizationId));

    lastIndex = start + match[0].length;
  }

  if (lastIndex < value.length) {
    nodes.push({
      type: 'text',
      value: value.slice(lastIndex),
    });
  }

  return nodes;
};

const createRemarkDocumentLinks = ({ organizationId }: Options): Plugin<[], Root> => {
  const plugin: Plugin<[], Root> = () => (tree) => {
    if (!organizationId) {
      return;
    }

    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || typeof index !== 'number') {
        return;
      }

      const replacementNodes = buildReplacementNodes(node.value, organizationId);

      if (!replacementNodes) {
        return;
      }

      parent.children.splice(index, 1, ...replacementNodes);

      return index + replacementNodes.length;
    });
  };

  return plugin;
};

export default createRemarkDocumentLinks;
